import { useEffect, useMemo, useState } from "react";

type Props = {
  src: string;
  alt: string;
  className?: string;
};

/**
 * Renders an image with a programmatic transparent background.
 * Designed for logos that were exported with a flat/light background.
 */
export function TransparentLogoImage({ src, alt, className }: Props) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  const cacheKey = useMemo(() => `transparent_logo_v4:${src}`, [src]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          if (!cancelled) setDataUrl(cached);
          return;
        }

        const img = new Image();
        img.crossOrigin = "anonymous";

        const loaded = await new Promise<HTMLImageElement>((resolve, reject) => {
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error("Failed to load logo image"));
          img.src = src;
        });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) throw new Error("No canvas context");

        canvas.width = loaded.naturalWidth;
        canvas.height = loaded.naturalHeight;
        ctx.drawImage(loaded, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imageData.data;

        // Detect likely background colors by sampling the border.
        // Works for both flat backgrounds and baked-in checkerboard exports.
        const quant = (v: number) => Math.round(v / 16) * 16;
        const keyOf = (r: number, g: number, b: number) => `${quant(r)},${quant(g)},${quant(b)}`;

        const counts = new Map<string, number>();
        const step = Math.max(4, Math.floor(Math.min(canvas.width, canvas.height) / 200));

        const samplePixel = (x: number, y: number) => {
          const idx = (y * canvas.width + x) * 4;
          const a = d[idx + 3];
          if (a < 200) return;
          const r = d[idx];
          const g = d[idx + 1];
          const b = d[idx + 2];
          const k = keyOf(r, g, b);
          counts.set(k, (counts.get(k) ?? 0) + 1);
        };

        // top & bottom rows
        for (let x = 0; x < canvas.width; x += step) {
          samplePixel(x, 0);
          samplePixel(x, canvas.height - 1);
        }
        // left & right cols
        for (let y = 0; y < canvas.height; y += step) {
          samplePixel(0, y);
          samplePixel(canvas.width - 1, y);
        }

        const topBg = [...counts.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([k]) => k.split(",").map(Number) as [number, number, number]);

        const dist = (r1: number, g1: number, b1: number, r2: number, g2: number, b2: number) => {
          const dr = r1 - r2;
          const dg = g1 - g2;
          const db = b1 - b2;
          return Math.sqrt(dr * dr + dg * dg + db * db);
        };

        const isBackgroundPixel = (r: number, g: number, b: number) => {
          const brightness = (r + g + b) / 3;

          // Only remove VERY DARK pixels (the dark background)
          // Keep anything that's not very dark - this preserves white text, orange icons, gray borders
          if (brightness > 50) return false;

          // If it matches one of the most common border colors (dark background), remove it
          if (
            topBg.some(([br, bg, bb]) => dist(r, g, b, br, bg, bb) < 30)
          ) {
            return true;
          }

          return false;
        };

        for (let i = 0; i < d.length; i += 4) {
          const r = d[i];
          const g = d[i + 1];
          const b = d[i + 2];
          const a = d[i + 3];

          if (a > 0 && isBackgroundPixel(r, g, b)) {
            d[i + 3] = 0;
          }
        }

        ctx.putImageData(imageData, 0, 0);

        const out = canvas.toDataURL("image/png");
        sessionStorage.setItem(cacheKey, out);
        if (!cancelled) setDataUrl(out);
      } catch {
        // If anything fails, just show the original image.
        if (!cancelled) setDataUrl(src);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [src, cacheKey]);

  return <img src={dataUrl ?? src} alt={alt} className={className} />;
}
